import { clsx } from 'clsx';
import { useMasterDocumentTranslation } from './i18n';
import type { Breakpoint, ContextStripProps } from './MasterDocument.types';

/**
 * Mapa breakpoint → clase responsive de Tailwind que oculta el item únicamente
 * dentro de ese rango de ancho. Las cadenas son literales para que el JIT de
 * Tailwind las detecte al hacer build.
 *
 *   xs  → <640px      |  sm → 640–767px
 *   md  → 768–1023px  |  lg → 1024–1279px
 *   xl  → 1280–1535px |  2xl → ≥1536px
 */
const HIDE_ON_BREAKPOINT_CLASS: Record<Breakpoint, string> = {
  xs: 'max-sm:hidden',
  sm: 'sm:max-md:hidden',
  md: 'md:max-lg:hidden',
  lg: 'lg:max-xl:hidden',
  xl: 'xl:max-2xl:hidden',
  '2xl': '2xl:hidden',
};

function hideClasses(breakpoints?: Breakpoint[]): string {
  if (!breakpoints?.length) return '';
  return breakpoints.map((bp) => HIDE_ON_BREAKPOINT_CLASS[bp]).join(' ');
}

/**
 * Franja de identidad del documento: centro de operación, tipo, número, fecha,
 * tercero… Se pinta como primera banda DENTRO de la tarjeta del detalle.
 *
 * **Rediseño (canon ADR-027).** Antes era una tira horizontal con los datos
 * separados por barras verticales y envolviendo con `flex-wrap`: al reflowear,
 * cada dato caía donde le tocaba y ni las etiquetas ni los valores compartían
 * eje, así que la franja se leía como una lista desordenada y no como la ficha
 * del documento. Ahora es una REJILLA: las columnas fijan el eje vertical, de
 * modo que todas las etiquetas se alinean entre sí y todos los valores también,
 * y el bloque sigue cuadrado con el resto del detalle al reflowear.
 *
 * La tipografía es la del canon: etiqueta de grupo en `text-[11px] font-bold
 * uppercase tracking-[0.4px] text-[#a1a1aa]` (§D.5) y valor en 14px/600. El
 * fondo es la banda `#f8f9fc` del canon de tabla (§E.1), que la separa del
 * formulario sin necesidad de una caja propia.
 *
 * La compañía NO viaja aquí: sube a la cabecera como badge junto al estado
 * (ver `SmartMasterDocument`), que es donde la pone el detalle de MasterCrud.
 *
 * @see MasterDocument.types - ContextStripProps / ContextStripItem
 */
export function ContextStrip({ items, currencyPanel, className }: ContextStripProps) {
  const { t } = useMasterDocumentTranslation();
  if (items.length === 0 && !currencyPanel) return null;

  return (
    <div
      className={clsx(
        'shrink-0 flex items-start justify-between gap-4',
        'bg-[#f8f9fc] dark:bg-dark-bg-secondary',
        'border-b border-border-secondary dark:border-dark-border-primary',
        'px-4 md:px-6 py-3',
        className,
      )}
      role="region"
      aria-label={t('master_document.contextStrip.aria')}
    >
      {/* `dl` y no una tira de `div`s: es literalmente una lista de pares
          término/descripción, y así un lector de pantalla anuncia cada valor
          con su etiqueta en vez de leer doce cadenas sueltas. */}
      <dl className="flex-1 min-w-0 grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-6 gap-x-6 gap-y-3">
        {items.map((item) => (
          <div
            key={item.label}
            className={clsx('flex flex-col gap-0.5 min-w-0', hideClasses(item.hideOnBreakpoints))}
          >
            <dt className="text-[11px] font-bold uppercase tracking-[0.4px] leading-4 text-[#a1a1aa] dark:text-dark-content-tertiary truncate">
              {item.label}
            </dt>
            <dd
              title={item.value || undefined}
              className={clsx(
                'text-sm font-semibold leading-5 truncate',
                item.accent
                  ? 'text-primary-custom-600 dark:text-primary-custom-500'
                  : 'text-content-primary dark:text-dark-content-primary',
                item.mono && 'tabular-nums',
              )}
            >
              {/* Un dato ausente se dice con una raya, no con un hueco: un valor
                  vacío deja la etiqueta suelta y parece que la ficha se rompió. */}
              {item.value?.trim() || '—'}
            </dd>
          </div>
        ))}
      </dl>

      {currencyPanel && (
        <div className="shrink-0 pl-4">
          {currencyPanel}
        </div>
      )}
    </div>
  );
}
